import { z } from 'zod';

export const ProjectStatusEnum = z.enum(['draft', 'active', 'archived']);
export type ProjectStatus = z.infer<typeof ProjectStatusEnum>;

export const ProjectMemberRoleEnum = z.enum(['owner', 'editor', 'viewer']);
export type ProjectMemberRole = z.infer<typeof ProjectMemberRoleEnum>;

// Roles assignable via invite — owner is set at create and never invited.
export const InvitableRoleEnum = z.enum(['editor', 'viewer']);
export type InvitableRole = z.infer<typeof InvitableRoleEnum>;

// The PM's hypothesis about who the product is for. customerSegment.role
// uses the same vocabulary as respondent.role so the existing graph
// segmentation filter surfaces the target segment automatically.
//
// Max-lengths mirror the backend `CreateProjectSchema` in
// `lambda/shared/schemas.ts` so the form catches over-long input inline
// instead of waiting for a 400 from the API. Bump in lockstep.
export const CustomerSegmentSchema = z.object({
  role: z.string().trim().min(1, 'Target role is required').max(200),
  industry: z.string().trim().max(200).optional(),
  description: z.string().trim().max(1000).optional(),
});
export type CustomerSegment = z.infer<typeof CustomerSegmentSchema>;

export const ProjectSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().optional(),
  // MVP journey capture-form fields, promoted onto an `idea` graph
  // node. GET /projects/{id} hydrates these back into the
  // response from the idea node, so the wire shape the frontend sees is
  // unchanged — but they're not stored on the project item itself.
  idea: z.string().optional(),
  customerSegment: CustomerSegmentSchema.optional(),
  aspiration: z.string().optional(),
  // Respondent framing — "what respondents will hear", authored at step 4 on
  // the idea node. framingSourceAspiration records which aspiration it was
  // written against (staleness); framingEdited stops auto-regeneration over
  // a hand edit.
  respondentFraming: z.string().optional(),
  openerBody: z.string().optional(),
  framingSourceAspiration: z.string().optional(),
  framingEdited: z.boolean().optional(),
  // Pointer to the `idea` graph node, set once the PM first captures any of
  // idea / aspiration / customerSegment. Absent on freshly-created projects
  // that haven't been through step 1 yet.
  ideaNodeId: z.string().optional(),
  // Step 2 done flag. Set by the PM via the "Mark step complete"
  // button when they're satisfied with the captured market_signal nodes.
  marketReviewed: z.boolean().optional(),
  // Step 3 done flag (same semantics, for assumption mapping).
  assumptionsReviewed: z.boolean().optional(),
  // Step 4 done flag — "enough dialogs for this iteration", a PM judgment.
  dialogsReviewed: z.boolean().optional(),
  // Step 5 done flag (same semantics, for review & synthesis).
  synthesisReviewed: z.boolean().optional(),
  // Step 6 done flag (same semantics, for capabilities / "what to build").
  capabilitiesReviewed: z.boolean().optional(),
  // Step 7 done flag (same semantics, for thesis alignment / pivot-or-persevere).
  thesisReviewed: z.boolean().optional(),
  status: ProjectStatusEnum.default('draft'),
  ownerId: z.string(),
  // The signed-in caller's role on this project, stamped by GET
  // /projects and GET /projects/{id}. Drives read-only mode for viewers and
  // the owner-only member-management UI. Absent on responses that predate it.
  callerRole: ProjectMemberRoleEnum.optional(),
  // Project-scoped consumption (invited conversations, research
  // runs). Sent by GET /projects/{id} and POST /projects only — the list and
  // PATCH omit it, and useUpdateProject merges rather than replaces so a PATCH
  // doesn't wipe it from the cache.
  usage: z
    .object({ conversations: z.number(), researchRuns: z.number() })
    .optional(),
  // The OWNER's plan limits, alongside `usage`. null = no governing plan
  // (every consuming action 402s); a null field inside = unlimited.
  limits: z
    .object({
      projects: z.number().nullable(),
      conversationsPerProject: z.number().nullable(),
      researchRunsPerProject: z.number().nullable(),
      dialogsPerProject: z.number().nullable(),
    })
    .nullable()
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectSchema = z.object({
  name: z.string().trim().min(1, 'Project name is required').max(200),
  description: z.string().trim().max(2000).optional(),
  idea: z.string().trim().max(2000).optional(),
  customerSegment: CustomerSegmentSchema.optional(),
  aspiration: z.string().trim().max(500).optional(),
});
export type CreateProjectInput = z.infer<typeof CreateProjectSchema>;

// Partial of CreateProjectSchema for PATCH /projects/{id}. All
// fields optional; null on a clearable field is the explicit "remove this
// value" signal (the backend deletes null keys on merge). name can't be
// nulled. Mirror in lockstep with the backend `UpdateProjectSchema` in
// `lambda/shared/schemas.ts`.
export const UpdateProjectSchema = z.object({
  name: z.string().trim().min(1, 'Project name is required').max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  idea: z.string().trim().max(2000).nullable().optional(),
  customerSegment: CustomerSegmentSchema.nullable().optional(),
  aspiration: z.string().trim().max(500).nullable().optional(),
  respondentFraming: z.string().trim().max(500).nullable().optional(),
  openerBody: z.string().trim().max(2000).nullable().optional(),
  marketReviewed: z.boolean().nullable().optional(),
  assumptionsReviewed: z.boolean().nullable().optional(),
  dialogsReviewed: z.boolean().nullable().optional(),
  synthesisReviewed: z.boolean().nullable().optional(),
  capabilitiesReviewed: z.boolean().nullable().optional(),
  thesisReviewed: z.boolean().nullable().optional(),
});
export type UpdateProjectInput = z.infer<typeof UpdateProjectSchema>;

// A project member as returned by GET /projects/{id}/members. Keyed by email
// (the membership key); no userId, since access is granted by email before the
// invitee ever signs in.
export const ProjectMemberSchema = z.object({
  email: z.string(),
  role: ProjectMemberRoleEnum,
  joinedAt: z.string(),
});
export type ProjectMember = z.infer<typeof ProjectMemberSchema>;

export const InviteMemberSchema = z.object({
  email: z.string().trim().email('Enter a valid email').max(254),
  role: InvitableRoleEnum,
});
export type InviteMemberInput = z.infer<typeof InviteMemberSchema>;
